export type FailureContext = {
  operation: string;
  environment: string;
  release: string;
  requestId: string;
};

function safeLabel(value: string) {
  return value.replace(/[^a-zA-Z0-9_./:[\]-]/g, "_").slice(0, 180);
}

export function failureProperties(context: FailureContext) {
  return {
    operation: safeLabel(context.operation),
    environment: safeLabel(context.environment),
    release: safeLabel(context.release),
    request_id: safeLabel(context.requestId),
    service: "nextjs-server",
    severity: "error",
  };
}

export function isExpectedRequestError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const data = (error as Error & { data?: unknown }).data;
  if (
    data &&
    typeof data === "object" &&
    "code" in data &&
    ["VALIDATION", "UNAUTHORIZED", "FORBIDDEN", "CARD_DECLINED"].includes(
      String(data.code),
    )
  )
    return true;
  return (
    /^(Unauthorized|Forbidden)$/.test(error.message) ||
    /^(NEXT_REDIRECT|NEXT_NOT_FOUND|NEXT_HTTP_ERROR_FALLBACK);/.test(
      String((error as Error & { digest?: string }).digest || ""),
    )
  );
}

/** Provider errors can embed full request bodies. Keep code frames and cause
 * structure, but never send arbitrary exception messages or custom properties.
 */
export function safeException(error: unknown, depth = 0): Error {
  const safe = new Error("Unexpected application failure");
  if (!(error instanceof Error)) return safe;
  // Custom error names can contain caller data too.
  safe.name =
    /^(Error|TypeError|RangeError|ReferenceError|SyntaxError|URIError|AggregateError)$/.test(
      error.name,
    )
      ? error.name
      : "Error";
  const frames = (error.stack || "")
    .split("\n")
    .filter((line) => /^\s+at /.test(line))
    .slice(0, 40)
    .map((line) =>
      line
        .replace(/https?:\/\/[^\s)]+/g, (url) => {
          try {
            const parsed = new URL(url);
            return `${parsed.origin}${parsed.pathname}`;
          } catch {
            return "[redacted-url]";
          }
        })
        .replace(/[\w.+-]+@[\w.-]+\.[a-zA-Z]{2,}/g, "[redacted-email]"),
    );
  safe.stack = `${safe.name}: ${safe.message}\n${frames.join("\n")}`;
  const cause = (error as Error & { cause?: unknown }).cause;
  if (cause && depth < 4) {
    Object.defineProperty(safe, "cause", {
      value: safeException(cause, depth + 1),
    });
  }
  return safe;
}

export async function boundedTelemetry(
  deliver: () => Promise<unknown>,
  timeoutMs = 2000,
) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      Promise.resolve().then(deliver),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } catch {
    // Telemetry is best effort. Never replace the original application failure.
  } finally {
    if (timer) clearTimeout(timer);
  }
}
